'use client';
/* eslint-disable @next/next/no-img-element */

import React, { useEffect, useState, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, query, orderBy, limit, where } from 'firebase/firestore';
import { db } from '@/lib/firebase/client';
import Link from 'next/link';
import {
  Filter, Flame, Newspaper, Search, Heart, X,
  Clock, ArrowRight, Sparkles, Tag
} from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';

// Blog-specific category pills (gradient-based for pills)
const BLOG_PILL_CATEGORIES = [
  { key: 'haircare', emoji: '💇', color: 'from-rose-500 to-pink-500' },
  { key: 'skincare', emoji: '🌿', color: 'from-emerald-500 to-teal-500' },
  { key: 'nails', emoji: '💅', color: 'from-amber-500 to-orange-500' },
  { key: 'makeup', emoji: '💄', color: 'from-purple-500 to-pink-500' },
  { key: 'wellness', emoji: '🧘', color: 'from-cyan-500 to-blue-500' },
  { key: 'trends', emoji: '✨', color: 'from-violet-500 to-indigo-500' },
  { key: 'business', emoji: '💼', color: 'from-slate-500 to-zinc-500' },
  { key: 'education', emoji: '📚', color: 'from-blue-500 to-indigo-500' },
  { key: 'deals', emoji: '🔥', color: 'from-rose-500 to-amber-500' },
  { key: 'beauty_news', emoji: '📰', color: 'from-blue-400 to-cyan-500' },
  { key: 'hair', emoji: '✂️', color: 'from-rose-500 to-red-500' },
  { key: 'nails_trends', emoji: '💅', color: 'from-amber-400 to-orange-500' },
  { key: 'hair_trends', emoji: '✂️', color: 'from-rose-400 to-pink-500' },
  { key: 'apparatus_cosmetology', emoji: '🔬', color: 'from-sky-500 to-blue-500' },
  { key: 'tattoo_news', emoji: '🖊️', color: 'from-indigo-500 to-purple-500' },
  { key: 'promo_deals', emoji: '🏷️', color: 'from-rose-500 to-amber-500' },
  { key: 'business_tips', emoji: '📊', color: 'from-slate-500 to-gray-500' },
  { key: 'injections_trends', emoji: '💉', color: 'from-rose-400 to-red-500' },
  { key: 'fitness_wellness', emoji: '🏋️', color: 'from-lime-500 to-emerald-500' },
];

export interface ArticleData {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  imageUrl: string;
  coverImageUrl?: string;
  category: string;
  articleType?: 'news' | 'promo';
  status?: string;
  pinned?: boolean;
  priority?: number;
  tags?: string[];
  authorName?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  createdAt: any;
}

// ─── Gradient fallbacks ──────────────────────────────────────────────────────
const CARD_GRADIENTS = [
  'from-pink-500 via-rose-500 to-red-400',
  'from-violet-500 via-purple-500 to-indigo-400',
  'from-amber-400 via-orange-500 to-red-400',
  'from-cyan-400 via-blue-500 to-indigo-500',
  'from-emerald-400 via-teal-500 to-cyan-500',
  'from-fuchsia-500 via-pink-500 to-rose-400',
];

function BlogContent() {
  const { t } = useTranslation('common');

  // Basic helper to allow fallback translations
  const bt = (key: string, fb: string) => t(key, fb);

  const [articles, setArticles] = useState<ArticleData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFavorites, setShowFavorites] = useState(false);
  const { user, userData, toggleLike } = useAuth();

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        let docs: ArticleData[] = [];
        try {
          // Try optimized query first (requires composite index)
          const q = query(
            collection(db, 'articles'),
            where('status', '==', 'approved'),
            orderBy('createdAt', 'desc'),
            limit(50)
          );
          const snap = await getDocs(q);
          const testWords = ['тест', 'טסט', 'בדיקה', 'test'];
          docs = snap.docs
            .map(d => ({ id: d.id, ...d.data() } as ArticleData))
            .filter(a => !testWords.some(w => a.title?.toLowerCase().includes(w)));
        } catch {
          // Fallback: fetch all and filter client-side if index is building
          const qFallback = query(collection(db, 'articles'), orderBy('createdAt', 'desc'), limit(50));
          const snapFallback = await getDocs(qFallback);
          const testWords = ['тест', 'טסט', 'בדיקה', 'test'];
          docs = snapFallback.docs
            .map(d => ({ id: d.id, ...d.data() } as ArticleData))
            .filter(a => (!a.status || a.status === 'approved') && !testWords.some(w => a.title?.toLowerCase().includes(w)));
        }

        // Sort: pinned first, then by priority desc, then by date desc
        docs.sort((a, b) => {
          if (a.pinned && !b.pinned) return -1;
          if (!a.pinned && b.pinned) return 1;
          if ((a.priority || 0) !== (b.priority || 0)) return (b.priority || 0) - (a.priority || 0);
          return 0; // Already ordered by createdAt desc from query
        });

        setArticles(docs);
      } catch (err) {
        console.error('Failed to load blog articles', err);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  // Filter articles
  const filtered = articles.filter(a => {
    if (showFavorites && (!userData?.likedArticles || !userData.likedArticles.includes(a.id))) return false;
    if (selectedCategory !== 'all' && a.category !== selectedCategory) return false;
    if (selectedType !== 'all' && a.articleType !== selectedType) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!a.title.toLowerCase().includes(q) && !(a.excerpt || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // Get used categories
  const usedCategories = [...new Set(articles.map(a => a.category).filter(Boolean))];

  const hasActiveFilters = searchQuery || selectedCategory !== 'all' || selectedType !== 'all' || showFavorites;

  const resetFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedType('all');
    setShowFavorites(false);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* ── Sticky Header (catalog style) ── */}
      <div className="sticky top-14 md:top-16 z-40 bg-background/95 backdrop-blur-xl border-b border-border/60">
        <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-2">

          {/* Title row — compact */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <Sparkles className="w-5 h-5 text-primary shrink-0" />
              <h1 className="text-lg sm:text-xl font-black truncate">{t('blog.title')}</h1>
            </div>
            <p className="text-xs text-muted-foreground font-medium hidden sm:block shrink-0">
              {!loading && <>{filtered.length} {t('blog.articles_count', 'статей')}</>}
            </p>
          </div>

          {/* Search + Type toggles row */}
          <div className="flex gap-2 mb-2">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder={t('blog.search_placeholder', 'Поиск статей...')}
                className="w-full bg-card border-2 border-border rounded-2xl pl-10 pr-10 py-3 text-sm font-medium outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all placeholder:text-muted-foreground/60"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Reset */}
            {hasActiveFilters && (
              <button onClick={resetFilters}
                className="flex items-center gap-1 px-3 py-2 bg-red-500/10 text-red-500 rounded-2xl border-2 border-red-500/20 text-sm font-bold hover:bg-red-500/20 transition-all shrink-0">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Type toggle pills */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none">
            {([
              { key: 'all', label: bt('admin_blog.tab_all', 'הכל'), icon: <Filter className="w-3.5 h-3.5" />, gradient: '' },
              { key: 'news', label: bt('admin_blog.type_news', 'חדשות'), icon: <Newspaper className="w-3.5 h-3.5" />, gradient: '' },
              { key: 'promo', label: bt('admin_blog.type_promo', 'מבצע'), icon: <Flame className="w-3.5 h-3.5" />, gradient: 'from-rose-500 to-amber-500' },
            ] as const).map(tp => (
              <button
                key={tp.key}
                onClick={() => { setSelectedType(tp.key); setShowFavorites(false); }}
                className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-bold whitespace-nowrap border-2 transition-all shrink-0 ${
                  !showFavorites && selectedType === tp.key
                    ? tp.gradient
                      ? `bg-linear-to-r ${tp.gradient} text-white border-rose-500 shadow-lg shadow-rose-500/20`
                      : 'bg-primary text-primary-foreground border-primary shadow-md shadow-primary/20'
                    : 'bg-card border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'
                }`}
              >
                {tp.icon} {tp.label}
              </button>
            ))}

            {/* Favorites */}
            {user && (
              <button
                onClick={() => { setShowFavorites(true); setSelectedType('all'); }}
                className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-bold whitespace-nowrap border-2 transition-all shrink-0 ${
                  showFavorites
                    ? 'bg-rose-500 text-white border-rose-500 shadow-lg shadow-rose-500/20'
                    : 'bg-card border-border text-rose-500 hover:border-rose-300'
                }`}
              >
                <Heart className={`w-3.5 h-3.5 ${showFavorites ? 'fill-current' : ''}`} /> {t('blog.my_favorites', 'Мои закладки')}
              </button>
            )}

            {/* Divider */}
            {usedCategories.length > 0 && (
              <div className="w-px bg-border mx-1 self-stretch shrink-0" />
            )}

            {/* Category pills (horizontal scroll, compact) */}
            {usedCategories.length > 0 && (
              <>
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-bold whitespace-nowrap border-2 transition-all shrink-0 ${
                    selectedCategory === 'all'
                      ? 'bg-primary/20 text-primary border-primary/40'
                      : 'bg-card border-border text-muted-foreground hover:border-primary/30'
                  }`}
                >
                  🌟 {t('catalog.all_categories', 'כל הקטגוריות')}
                </button>
                {BLOG_PILL_CATEGORIES.filter(c => usedCategories.includes(c.key)).map(cat => (
                  <button
                    key={cat.key}
                    onClick={() => setSelectedCategory(cat.key)}
                    className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-bold whitespace-nowrap border-2 transition-all shrink-0 ${
                      selectedCategory === cat.key
                        ? `bg-linear-to-r ${cat.color || 'from-primary to-primary/80'} text-white border-transparent shadow-lg`
                        : 'bg-card border-border text-muted-foreground hover:border-primary/30'
                    }`}
                  >
                    {cat.emoji} {t(`blog_categories.${cat.key}`, t(`categories.${cat.key}`, cat.key))}
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="max-w-[1400px] mx-auto px-3 py-3">



        {/* Loading skeletons */}
        {loading && (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-card rounded-2xl animate-pulse border border-border overflow-hidden">
                <div className="aspect-[16/10] bg-secondary/50" />
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-secondary rounded w-3/4" />
                  <div className="h-3 bg-secondary rounded w-full" />
                  <div className="h-3 bg-secondary rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center bg-card/40 backdrop-blur-md rounded-3xl border border-border/50">
            <span className="text-5xl mb-3">📰</span>
            <h2 className="text-xl font-black mb-2 text-foreground">{t('blog.empty_title')}</h2>
            <p className="text-muted-foreground text-sm mb-4">{t('blog.empty_desc')}</p>
            {hasActiveFilters && (
              <button onClick={resetFilters}
                className="px-5 py-2.5 bg-primary text-primary-foreground rounded-full font-bold text-sm hover:bg-primary/90 transition-all">
                {t('catalog.reset', 'Сбросить')}
              </button>
            )}
          </div>
        )}

        {/* ─── Card Grid ─── */}
        {!loading && filtered.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {filtered.map((article, idx) => {
              const isLiked = userData?.likedArticles?.includes(article.id);
              const isPromo = article.articleType === 'promo';
              const cover = article.coverImageUrl || article.imageUrl;
              const gradient = CARD_GRADIENTS[idx % CARD_GRADIENTS.length];
              // First article spans 2 cols on large screens for slight emphasis
              const isFirst = idx === 0;

              return (
                <div key={article.id} className={`group relative ${isFirst ? 'sm:col-span-2 lg:col-span-2' : ''}`}>
                  <Link href={`/blog/article?id=${article.id}`} className="block h-full">
                    <article className={`relative rounded-2xl overflow-hidden border bg-card shadow-sm hover:shadow-xl hover:shadow-primary/10 transition-all duration-300 hover:-translate-y-1 h-full flex flex-col ${
                      isPromo ? 'border-rose-500/30 hover:border-rose-500/60' : 'border-border/60 hover:border-primary/50'
                    }`}>
                      {/* Photo */}
                      <div className={`relative ${isFirst ? 'aspect-[16/9]' : 'aspect-[16/10]'} bg-linear-to-br ${gradient} overflow-hidden shrink-0`}>
                        {cover ? (
                          <img src={cover} alt={article.title}
                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading={isFirst ? 'eager' : 'lazy'}
                            style={{ objectPosition: 'center center' }} />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-white/40 text-5xl">
                            {isPromo ? '🔥' : '📰'}
                          </div>
                        )}
                        <div className="absolute inset-0 bg-linear-to-t from-black/50 via-transparent to-transparent" />

                        {/* Top badges */}
                        <div className="absolute top-2 left-2 flex flex-col gap-1 z-10">
                          {article.pinned && (
                            <div className="flex items-center gap-1 px-2 py-0.5 bg-amber-500/90 backdrop-blur-sm text-white text-[10px] font-black rounded-full shadow-lg">
                              📌 {bt('blog.pinned', 'נועץ')}
                            </div>
                          )}
                          {isPromo ? (
                            <div className="flex items-center gap-1 px-2 py-0.5 bg-linear-to-r from-rose-500 to-amber-500 text-white text-[10px] font-black rounded-full shadow-lg badge-shimmer">
                              <Flame className="w-3 h-3" /> {bt('admin_blog.type_promo', 'מבצע')}
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 px-2 py-0.5 bg-blue-500/80 backdrop-blur-sm text-white text-[10px] font-black rounded-full shadow-lg">
                              <Newspaper className="w-3 h-3" /> {bt('admin_blog.type_news', 'חדשות')}
                            </div>
                          )}
                        </div>

                        {/* Like */}
                        {user && (
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleLike(article.id); }}
                            className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-all shadow-lg z-10 backdrop-blur-md ${
                              isLiked ? 'bg-red-500 text-white scale-110' : 'bg-white/20 text-white hover:bg-red-500 hover:scale-110'
                            }`}>
                            <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
                          </button>
                        )}

                        {/* Bottom category + date overlay */}
                        <div className="absolute bottom-2 left-2 z-10">
                          {article.category && (
                            <span className="inline-flex items-center gap-1 bg-black/40 backdrop-blur-md text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                              <Tag className="w-2.5 h-2.5" />
                              {t(`blog_categories.${article.category.toLowerCase()}`, t(`categories.${article.category.toLowerCase()}`, article.category))}
                            </span>
                          )}
                        </div>
                        <div className="absolute bottom-2 right-2 z-10">
                          <span className="inline-flex items-center gap-1 bg-black/40 backdrop-blur-md text-white/80 text-[10px] font-bold px-2 py-0.5 rounded-full">
                            <Clock className="w-2.5 h-2.5" />
                            {article.createdAt?.toDate ? article.createdAt.toDate().toLocaleDateString() : t('blog.published_recently', 'חדש')}
                          </span>
                        </div>
                      </div>

                      {/* Info */}
                      <div className="p-3 flex flex-col flex-1">
                        <h3 className={`${isFirst ? 'text-base' : 'text-sm'} font-black leading-tight line-clamp-2 mb-1 group-hover:text-primary transition-colors`}>
                          {article.title}
                        </h3>
                        <p className="text-xs text-muted-foreground line-clamp-2 mb-2 leading-relaxed flex-1">
                          {article.excerpt || article.content?.substring(0, 100)}
                        </p>

                        <div className="flex items-center justify-between pt-2 border-t border-border/50 mt-auto">
                          <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
                            {article.authorName && (
                              <span className="text-[10px] text-muted-foreground font-semibold truncate">
                                {article.authorName}
                              </span>
                            )}
                            {article.tags && article.tags.length > 0 && (
                              <span className="text-[10px] text-primary/60 font-bold truncate">
                                #{article.tags[0]}
                              </span>
                            )}
                          </div>
                          <span className="text-[11px] font-bold text-primary flex items-center gap-0.5 shrink-0 group-hover:gap-1.5 transition-all">
                            {t('blog.read_more')} <ArrowRight className="w-3 h-3" />
                          </span>
                        </div>
                      </div>
                    </article>
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function BlogPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-background">
        <div className="sticky top-14 md:top-16 z-40 bg-background/95 border-b border-border/60">
          <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-3">
            <div className="h-7 w-48 bg-card rounded animate-pulse mb-3" />
            <div className="h-12 bg-card border-2 border-border rounded-2xl animate-pulse mb-3" />
            <div className="flex gap-2 overflow-hidden pb-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-10 w-24 bg-card border-2 border-border rounded-full animate-pulse shrink-0" />
              ))}
            </div>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto px-3 py-3">
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-card rounded-2xl animate-pulse border border-border overflow-hidden">
                <div className="aspect-[16/10] bg-secondary/50" />
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-secondary rounded w-3/4" />
                  <div className="h-3 bg-secondary rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    }>
      <BlogContent />
    </Suspense>
  );
}
